import { useEffect, useState } from "react";
import api from "@/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus } from "lucide-react";

const BED_TYPES = [
  { value: "normal", label: "Normal" },
  { value: "semi-private", label: "Semi-Private" },
  { value: "private", label: "Private" },
];

export default function NewBedModal({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [wards, setWards] = useState([]);
  const [f, setF] = useState({ ward_id: "", bed_number: "", bed_type: "normal" });

  useEffect(() => {
    if (open) api.get("/ipd/wards").then((r) => setWards(r.data)).catch(() => {});
  }, [open]);

  const submit = async () => {
    if (!f.ward_id || !f.bed_number) { toast.error("Ward and bed number are required"); return; }
    setSaving(true);
    try {
      await api.post("/ipd/beds", f);
      toast.success("Bed created");
      setOpen(false); setF({ ward_id: "", bed_number: "", bed_type: "normal" });
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid="ipd-new-bed-btn">
          <Plus size={14} className="mr-1" /> New Bed
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="ipd-new-bed-modal">
        <DialogHeader><DialogTitle>New Bed</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <div><Label>Ward *</Label>
            <Select value={f.ward_id} onValueChange={(v) => setF({ ...f, ward_id: v })}>
              <SelectTrigger data-testid="ipd-bed-ward"><SelectValue placeholder="Select ward" /></SelectTrigger>
              <SelectContent>{wards.map((w) => <SelectItem key={w.id} value={w.id}>{w.ward_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Bed number *</Label>
            <Input value={f.bed_number} onChange={(e) => setF({ ...f, bed_number: e.target.value })}
                   placeholder="e.g. A-101" data-testid="ipd-bed-number" /></div>
          <div><Label>Bed type</Label>
            <Select value={f.bed_type} onValueChange={(v) => setF({ ...f, bed_type: v })}>
              <SelectTrigger data-testid="ipd-bed-type"><SelectValue /></SelectTrigger>
              <SelectContent>{BED_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-sky-500 hover:bg-sky-600 text-white"
                  data-testid="ipd-bed-submit">{saving ? "Saving…" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
